import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Coffee, Clock, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../contexts/SettingsContext';

const CreateBreakModal = ({ isOpen, onClose, onCreate }) => {
  const { t } = useTranslation('breaks');
  const daysShort = t('daysShort', { returnObjects: true });
  const daysOfWeek = [
    { key: 'monday', label: t('daysFull.monday'), short: daysShort[0] },
    { key: 'tuesday', label: t('daysFull.tuesday'), short: daysShort[1] },
    { key: 'wednesday', label: t('daysFull.wednesday'), short: daysShort[2] },
    { key: 'thursday', label: t('daysFull.thursday'), short: daysShort[3] },
    { key: 'friday', label: t('daysFull.friday'), short: daysShort[4] },
    { key: 'saturday', label: t('daysFull.saturday'), short: daysShort[5] },
    { key: 'sunday', label: t('daysFull.sunday'), short: daysShort[6] },
  ];
  const { preferences } = useSettings();
  const globalSoundEnabled = preferences.playNotificationSound !== false;

  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [startTimeError, setStartTimeError] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [activeDays, setActiveDays] = useState({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  });
  const [soundEnabled, setSoundEnabled] = useState(globalSoundEnabled);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('createModal.nameRequired'));
      return;
    }
    if (!startTime) {
      setError(t('createModal.startTimeRequired'));
      return;
    }

    const normalizedTime = normalizeTimeString(startTime);
    if (!normalizedTime) {
      setStartTimeError(t('createModal.timeError'));
      return;
    }

    onCreate({
      name,
      startTime: normalizedTime,
      durationMinutes,
      activeDays,
      soundEnabled: globalSoundEnabled ? soundEnabled : false,
    });
    setName('');
    setStartTime('10:00');
    setStartTimeError('');
    setDurationMinutes(15);
    setActiveDays({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    });
    setSoundEnabled(globalSoundEnabled);
    setError('');
    onClose();
  };

  const toggleDay = (dayKey) => {
    setActiveDays((prev) => ({
      ...prev,
      [dayKey]: !prev[dayKey],
    }));
  };

  useEffect(() => {
    if (!globalSoundEnabled) {
      setSoundEnabled(false);
    }
  }, [globalSoundEnabled]);

  const selectAllWeekdays = () => {
    setActiveDays({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    });
  };

  const selectAllDays = () => {
    const allTrue = Object.values(activeDays).every((day) => day === true);
    const newState = {
      monday: !allTrue,
      tuesday: !allTrue,
      wednesday: !allTrue,
      thursday: !allTrue,
      friday: !allTrue,
      saturday: !allTrue,
      sunday: !allTrue,
    };
    setActiveDays(newState);
  };

  const normalizeTimeString = (value) => {
    if (!value) return null;
    const trimmed = value.trim();
    const match = trimmed.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    return null;
  };

  const handleTimeChange = (value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      setStartTime('');
      setStartTimeError('');
      return;
    }

    if (digits.length > 4) {
      return;
    }

    const hourPart = digits.slice(0, 2);
    const minutePart = digits.slice(2);

    if (hourPart.length === 1 && parseInt(hourPart, 10) > 2) {
      return;
    }

    if (hourPart.length === 2 && parseInt(hourPart, 10) > 23) {
      return;
    }

    if (minutePart.length > 0) {
      if (minutePart.length > 2) {
        return;
      }
      if (minutePart.length === 2 && parseInt(minutePart, 10) > 59) {
        return;
      }
    }

    let formatted = hourPart;
    if (hourPart.length === 2 && minutePart.length === 0) {
      formatted = `${hourPart}:`;
    }
    if (minutePart.length > 0) {
      formatted = `${hourPart}:${minutePart}`;
    }

    setStartTime(formatted);
    setStartTimeError('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100000] flex items-start justify-center pt-4 sm:pt-6 md:pt-8 pb-2 sm:pb-4 px-2 sm:px-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: -20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: -20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="relative bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-base)] p-3 sm:p-4 md:p-5 rounded-lg shadow-xl border border-white/10 dark:border-white/[0.07] w-full max-w-[95vw] sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-[85vh] sm:max-h-[80vh] md:max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1 rounded-full text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-white/5 hover:text-[var(--color-text-secondary)] dark:hover:text-[var(--color-text-primary)] transition-colors"
              aria-label={t('createModal.close')}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)]">{t('createModal.title')}</h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
              {/* Mola Adı ve Başlangıç Saati - Yan Yana */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label htmlFor="breakName" className="block text-xs font-medium text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] mb-1">
                    {t('createModal.nameLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="breakName"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setError('');
                    }}
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm bg-[#161f31] border border-white/10 dark:border-white/10 rounded-md text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] dark:placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[#3d8ef0]/50 focus:border-[#3d8ef0] transition-all"
                    placeholder={t('createModal.namePlaceholder')}
                    autoFocus
                  />
                  {error && <p className="text-red-500 text-xs mt-0.5">{error}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] mb-1">
                    {t('createModal.startTimeLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={startTime}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    onBlur={() => {
                      const normalized = normalizeTimeString(startTime);
                      if (normalized) {
                        setStartTime(normalized);
                        setStartTimeError('');
                      } else {
                        setStartTimeError(t('createModal.timeError'));
                      }
                    }}
                    placeholder="HH:MM"
                    required
                    maxLength={5}
                    className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm bg-[#161f31] border ${
                      startTimeError ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : 'border-white/10 dark:border-white/10'
                    } rounded-md text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] dark:placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[#3d8ef0]/50 focus:border-[#3d8ef0] transition-all`}
                    inputMode="numeric"
                    pattern="^\d{2}:\d{2}$"
                  />
                  {startTimeError && <p className="text-red-500 text-xs mt-0.5">{startTimeError}</p>}
                </div>
              </div>

              {/* Süre ve Bildirim Sesi - Yan Yana */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label htmlFor="duration" className="block text-xs font-medium text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] mb-1">
                    {t('createModal.durationLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="duration"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    max="480"
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm bg-[#161f31] border border-white/10 dark:border-white/10 rounded-md text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[#3d8ef0]/50 focus:border-[#3d8ef0] transition-all"
                    required
                  />
                </div>
                <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-md bg-[#161f31] border border-white/10 dark:border-white/10">
                  <div className="flex items-center gap-2">
                    {globalSoundEnabled && soundEnabled ? (
                      <Volume2 className="w-4 h-4 text-[#3d8ef0]" />
                    ) : (
                      <VolumeX className="w-4 h-4 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                    )}
                    <label htmlFor="soundEnabled" className="text-xs font-medium text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] cursor-pointer">
                      {t('createModal.notificationSoundLabel')}
                    </label>
                  </div>
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (!globalSoundEnabled) return;
                      setSoundEnabled(!soundEnabled);
                    }}
                    disabled={!globalSoundEnabled}
                    className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                      globalSoundEnabled && soundEnabled ? 'bg-[#2563eb]' : 'bg-gray-300 dark:bg-[#24354f] opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <motion.div
                      animate={{ x: globalSoundEnabled && soundEnabled ? 20 : 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className="absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--color-bg-base)] rounded-full shadow-md"
                    />
                  </motion.button>
                </div>
                {!globalSoundEnabled && (
                  <p className="mt-1 text-[10px] text-amber-500">
                    {t('createModal.enableSoundFirst')}
                  </p>
                )}
              </div>

              {/* Aktif Günler */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)]">
                    {t('createModal.activeDaysLabel')}
                  </label>
                  <div className="flex gap-1.5">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={selectAllWeekdays}
                      className="px-2 py-1 text-xs font-medium rounded-md bg-[#161f31] hover:bg-[#24354f] dark:hover:bg-[#24354f] text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] transition-all duration-200 border border-white/10 dark:border-white/10"
                    >
                      {t('createModal.weekdaysButton')}
                    </motion.button>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={selectAllDays}
                      className="px-2 py-1 text-xs font-medium rounded-md bg-[#161f31] hover:bg-[#24354f] dark:hover:bg-[#24354f] text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] transition-all duration-200 border border-white/10 dark:border-white/10"
                    >
                      {t('createModal.allButton')}
                    </motion.button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {daysOfWeek.map((day) => (
                    <motion.button
                      key={day.key}
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => toggleDay(day.key)}
                      className={`p-2 rounded-md border transition-all duration-200 text-xs font-medium ${
                        activeDays[day.key]
                          ? 'bg-[#1e2d47] border-[#3d8ef0]/40 text-[#c8d8eb]'
                          : 'bg-[#161f31] border-white/10 dark:border-white/10 text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] hover:border-gray-400 dark:hover:border-white/20'
                      }`}
                    >
                      {day.short}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 pt-2">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-sm rounded-md bg-[#161f31] border border-white/10 dark:border-white/10 text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)] hover:bg-[#24354f] dark:hover:bg-[#24354f] transition-all font-medium"
                >
                  {t('common:cancel')}
                </motion.button>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-sm rounded-md bg-[#2563eb] hover:bg-[#3d8ef0] text-white font-medium transition-all"
                >
                  {t('createModal.create')}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateBreakModal;

